import React, { useState, useEffect, useRef, useMemo } from "react";
import { Flex, Text, Card, Table, Badge, Tooltip, Button, Box, HoverCard } from "@radix-ui/themes";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { useLiveAccessPoints } from "hooks";
import { useLocationContext } from "context";
import { StackedStatusBar } from "components/charts/StackedStatusBar";
import {  useNavigate } from "react-router-dom";


// Custom component to handle text truncation and tooltip for column header text
const TruncatedText = ({ children, size, weight, color }) => {
  const text = typeof children === 'string' ? children : String(children);
  const [isTruncated, setIsTruncated] = useState(false);
  const containerRef = useRef(null);
  
  useEffect(() => {
    const checkTruncation = () => {
      const container = containerRef.current;
      if (container) {
        setIsTruncated(container.scrollWidth > container.clientWidth);
      }
    };
    
    // Initial check
    checkTruncation();
    
    // Recheck on window resize
    window.addEventListener('resize', checkTruncation);
    
    return () => window.removeEventListener('resize', checkTruncation);
  }, [text]);
  
  const textElement = (
    <Box as="div" 
      ref={containerRef} 
      style={{
        width: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      }}
    >
      <Text 
        size={size} 
        weight={weight}
        color={color}
      >
        {text}
      </Text>
    </Box>
  );
  
  return isTruncated ? (
    <Tooltip content={text}>
      {textElement}
    </Tooltip>
  ) : textElement;
};

export default function Locations() {
  const navigate = useNavigate();
  const { locationsData } = useLocationContext();
  const { liveAccessPointsStatus } = useLiveAccessPoints(locationsData);

  // Sort the liveAccessPointsStatus array alphabetically by name
  const sortedAccessPointsStatus = useMemo(() => {
    return liveAccessPointsStatus 
      ? [...liveAccessPointsStatus].sort((a, b) => 
          (a.name || "").localeCompare(b.name || ""))
      : [];
  }, [liveAccessPointsStatus]);

  const [stackedStatusBarData, setStackedStatusBarData] = useState({
    online: 0,
    offline: 0,
    partially: 0,
  });

  useEffect(() => {
    if (!sortedAccessPointsStatus) return;

    const newStats = sortedAccessPointsStatus.reduce(
      (acc, ap) => {
        if (ap.online_access_points_count > 0 && ap.offline_access_points_count > 0) {
          acc.partially += 1;
        } else if (ap.online_access_points_count > 0 && ap.offline_access_points_count === 0) {
          acc.online += 1;
        } else {
          acc.offline += 1;
        }
        return acc;
      },
      { online: 0, offline: 0, partially: 0 }
    );

    setStackedStatusBarData(newStats);
  }, [sortedAccessPointsStatus]);

  return (
    <Flex direction="column" gap="5" p="5" style={{ alignSelf: "stretch", alignItems: "flex-start", flex: "1 0 0", 
      height: "100%" }}>
      <Button size="1" variant="soft" color="gray"
        onClick={() => navigate("/organisation/locations")}
        style={{ alignSelf: "flex-end"}}>
        Edit locations
      </Button>
      {/* Location Status */}
      <Card style={{ display: "flex", alignSelf: "stretch", padding: "var(--space-4)", flexDirection: "column", 
        alignItems: "flex-start", gap: "var(--space-4)" }}>
        <Flex direction="column" gap="4" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
          <Text size="2" weight="medium">
            Location status
          </Text>
          <StackedStatusBar stackedStatusBarData={stackedStatusBarData} />
        </Flex>
      </Card>
      {/* Access Points Details */}
      <Card style={{ display: "flex", alignSelf: "stretch", padding: "var(--space-4)", flexDirection: "column",
        alignItems: "flex-start", gap: "var(--space-4)", overflow: "scroll", flex: '1 0 0' }}>
        <Table.Root layout="fixed" style={{ width: "100%", height: "100%"}}>
          <Table.Header style={{ position: "sticky", top: "0", zIndex: "1", backgroundColor: "var(--color-panel-solid)" }}>
            <Table.Row align="center">
              <Table.ColumnHeaderCell>
                <TruncatedText size="2" weight="medium">Location</TruncatedText>
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>
                <TruncatedText size="2" weight="medium">Brand</TruncatedText>
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>
                <TruncatedText size="2" weight="medium">Access points</TruncatedText>
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>
                <TruncatedText size="2" weight="medium">Provider</TruncatedText>
              </Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {sortedAccessPointsStatus.map((location, index) => (
              <Table.Row key={index} align="center">
                {/* Location's Name Cell */}
                <Table.Cell>
                  <Flex className="text-container" direction="column" align="start">
                    <Flex align="center" justify="flex-start" gap="2" style={{ width: "100%" }}>
                      {location.online_access_points_count > 0 && location.offline_access_points_count > 0 ? (
                        <Tooltip content="Partially online">
                          <Box as="div" style={{background: "var(--amber-9)", height: "8px", width: "8px", borderRadius: "4px"}}/>
                        </Tooltip>
                      ) : location.online_access_points_count > 0 && location.offline_access_points_count === 0 ? (
                        <Tooltip content="Online">
                          <Box as="div" style={{background: "var(--grass-9)", height: "8px", width: "8px", borderRadius: "4px"}}/>
                        </Tooltip>
                      ) : (
                        <Tooltip content="Offline">
                          <Box as="div" style={{background: "var(--red-9)", height: "8px", width: "8px", borderRadius: "4px"}}/>
                        </Tooltip>
                      )}
                      <TruncatedText size="2" weight="regular">{location.name}</TruncatedText>
                    </Flex>
                    <TruncatedText weight="regular" size="1" color="gray">{location.address}</TruncatedText>
                  </Flex>
                </Table.Cell>
                {/* Brand Cell */}
                <Table.Cell>
                  <TruncatedText size="1" weight="regular" color="gray">
                    {location.brand?.name || "No brand"}
                  </TruncatedText>
                </Table.Cell>
                {/* Access Point Status Cell */}
                <Table.Cell>
                  { location.access_points_details?.length > 0 ? (
                  <HoverCard.Root>
                    <HoverCard.Trigger>
                      <Flex gap="1" align="center" justify="flex-start">
                        {location.online_access_points_count > 0 ? (
                          <Badge variant="outline" color="grass" size="1" style={{ cursor: 'default'}}>
                            <Text size="1" weight="medium">
                              {`${location.online_access_points_count} Online`}
                            </Text>
                          </Badge>
                        ) : null}
                        {location.offline_access_points_count > 0 ? (
                          <Tooltip content="Ensure your Wi-Fi Access Point is securely connected to your main router using a PoE injector.">
                            <Badge variant="outline" color="red" size="1" style={{ cursor: 'default'}}>
                              <InfoCircledIcon style={{ width: "12px", height: "12px", color: "var(--red-a11)" }} />
                              <Text size="1" weight="medium">
                                {`${location.offline_access_points_count} Offline`}
                              </Text>
                            </Badge>
                          </Tooltip>
                        ) : null}
                      </Flex>
                    </HoverCard.Trigger>
                    <HoverCard.Content maxHeight="300px" style={{padding: 'var(--space-3)'}}>
                      <Flex direction="column" gap="2">
                          {location.access_points_details?.map((ap, apIndex) => (
                              <Flex key={apIndex} justify="between" align="center" gap="3">
                                  {ap.is_online ? (
                                      <Box as="div" style={{background: "var(--grass-9)", height: "8px", width: "8px", borderRadius: "4px"}}/>
                                  ) : (
                                    <Box as="div" style={{background: "var(--red-9)", height: "8px", width: "8px", borderRadius: "4px"}}/>
                                  )}
                                  <Text size="1">{ap.mac_address.toUpperCase()}</Text>
                              </Flex>
                          ))}
                      </Flex>
                    </HoverCard.Content>
                  </HoverCard.Root>
                  ) : (
                    <TruncatedText size="1" weight="medium" color="gray">
                      No access points
                    </TruncatedText>
                  )}
                </Table.Cell>
                {/* Provider's Name Cell */}
                <Table.Cell>
                {Array.isArray(location.providers) && location.providers.length > 0 && location.providers[0] !== "" ? (
                  <TruncatedText size="1" weight="regular" color="gray">
                    {location.providers.length === 1
                      ? location.providers[0]
                      : location.providers.length === 2
                      ? `${location.providers[0]} & ${location.providers[1]}`
                      : location.providers
                          .slice(0, -1)
                          .join(", ") + ` & ${location.providers[location.providers.length - 1]}`
                    }
                  </TruncatedText>
                ) : (
                  <TruncatedText size="1" weight="regular" color="gray">
                    No provider
                  </TruncatedText>
                )}
              </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Card>
    </Flex>
  );
}
